import React, { useMemo, useState } from "react";
import { CardContent, Tooltip } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../LoadingSpinner";
import {
  useGetDownloaderReportsQuery
} from "../../../Services/reports/tickets/ReportTickets";
import { toast } from "react-toastify";

const formatReportFilterDate = (value: unknown) => {
  if (!value) return "-";
  const raw = String(value).trim();
  const isoDateMatch = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
  }
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

const formatDateTime = (value: unknown) => {
  if (!value) return "-";
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return String(value);

  const pad = (n: number) => String(n).padStart(2, "0");
  const day = pad(date.getDate());
  const month = pad(date.getMonth() + 1);
  const year = date.getFullYear();
  const hours = pad(date.getHours());
  const minutes = pad(date.getMinutes());

  const raw = String(value).trim();
  const isDateOnly = /^\d{4}-\d{1,2}-\d{1,2}$/.test(raw);
  if (isDateOnly) {
    return `${day}/${month}/${year}`;
  }

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

const formatFileSize = (bytes: unknown) => {
  if (bytes == null || bytes === "") return "-";
  const num = Number(bytes);
  if (Number.isNaN(num) || num < 0) return "-";
  if (num < 1024) return "1 KB";

  let size = num / 1024;
  let unitIndex = 0;
  const units = ["KB", "MB", "GB", "TB"];

  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }

  const formatted =
    size >= 10 || unitIndex === 0
      ? Math.round(size).toString()
      : size.toFixed(1);

  return `${formatted} ${units[unitIndex]}`;
};

const getReportDateFilters = (item: any) => {
  const payload =
    item?.request_payload ??
    item?.filters ??
    item?.metadata?.filters ??
    {};

  return {
    fromDate: payload.from_date ?? payload.gte ?? payload.date ?? null,
    toDate: payload.to_date ?? payload.lte ?? payload.date ?? null,
  };
};

const normalizeStatus = (status: unknown) => {
  if (!status) return "-";
  const value = String(status).toLowerCase();
  if (value === "completed" || value === "success" || value === "ready") {
    return "Completed";
  }
  if (value === "pending" || value === "queued" || value === "processing") {
    return "In Progress";
  }
  if(value === "uploading"){
    return "Uploading";
  }
  if (value === "failed_processing" || value === "error") {
    return "Failed";
  }
  return String(status);
};

const DownloaderReportList = () => {
  const navigate = useNavigate();
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 20,
    page: 0,
  });

  const { data, isLoading, isFetching, error } =
    useGetDownloaderReportsQuery(
      {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
      },
      { refetchOnMountOrArgChange: true }
    );

  
  const [downloadingReportId, setDownloadingReportId] = useState<
    number | string | null
  >(null);

  const results = useMemo(() => {
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data)) return data;
    return [];
  }, [data]);

  const totalRows = useMemo(() => {
    const value =
      data?.count ?? data?.total_rows ?? data?.total ?? results.length;
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  }, [data, results.length]);

  React.useEffect(() => {
    const serverPage = data?.current_page ?? data?.page;
    const serverPageSize = data?.page_size;
    if (serverPage == null && serverPageSize == null) return;

    setPaginationModel((prev) => ({
      page: Math.max(0, Number(serverPage ?? prev.page + 1) - 1),
      pageSize: Number(serverPageSize ?? prev.pageSize),
    }));
  }, [data?.current_page, data?.page, data?.page_size]);

  const rows = useMemo(
    () =>
      results.map((item: any, index: number) => {


        return {
        id: item.id ?? item.pk ?? index + 1,
        srNo: (paginationModel.page) * paginationModel.pageSize + index + 1,
        report_name:
          item.report_name ??
          item.report_title ??
          item.name ??
          item.report_type ??
          "-",
        from_date: item.from_date,
        to_date: item.to_date,
        status: item.status ?? item.state ?? "-",
        created_at:
          item.created_at ??
          item.requested_at ??
          item.created ??
          item.queued_at,
    
        total_rows: item.total_rows ?? item.row_count ?? item.total,
        presigned_url_valid_till:
          item.presigned_url_valid_till ??
          item.report_valid_till ??
          item.valid_till,
        file_size_bytes: item.file_size_bytes ?? item.file_size,
        presigned_url: item.presigned_url ?? "",
        raw: item,
      };
      }),
    [results, paginationModel.page, paginationModel.pageSize]
  );

  const handleDownloadFile = async (row: (typeof rows)[number]) => {
    const downloadUrl = row.presigned_url;
    if (!downloadUrl) {
      toast.error("Download file is not available yet.");
      return;
    }

    const reportId = row.id;
    if (reportId == null || reportId === "") {
      toast.error("Report id is missing.");
      return;
    }

    window.open(downloadUrl, "_blank", "noopener,noreferrer");

    setDownloadingReportId(reportId);
    setTimeout(() => setDownloadingReportId(null), 600);
  };

  const columns: GridColDef[] = [
    {
      field: "srNo",
      headerName: "Sr.no",
      width: 70,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "report_name",
      headerName: "Report Name",
      flex: 1,
      minWidth: 180,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "from_date",
      headerName: "From Date",
      flex: 1,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) => formatReportFilterDate(row.from_date),
    },
    {
      field: "to_date",
      headerName: "To Date",
      flex: 1,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) => formatReportFilterDate(row.to_date),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 120,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) => normalizeStatus(row.status),
    },
    {
      field: "created_at",
      headerName: "Requested At",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) => formatDateTime(row.created_at),
    },
    {
      field: "presigned_url_valid_till",
      headerName: "Report Valid Till",
      flex: 1,
      minWidth: 150,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) => formatDateTime(row.presigned_url_valid_till),
    },
    {
      field: "total_rows",
      headerName: "Total Rows",
      flex: 1,
      minWidth: 100,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) => {
        const count = row.total_rows;
        if (count == null || count === "") return "-";
        return Number(count).toLocaleString();
      },
    },
    {
      field: "file_size_bytes",
      headerName: "File Size",
      flex: 1,
      minWidth: 90,
      align: "center",
      headerAlign: "center",
      valueGetter: (_value, row) => formatFileSize(row.file_size_bytes),
    },
    {
      field: "actions",
      headerName: "Action",
      width: 110,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const normalizedStatus = normalizeStatus(params.row.status);
        if (normalizedStatus === "Failed") {
          return (
            <Tooltip title="Refetch report again">
              <span>
                <i
                  className="bi bi-info-circle text-dark fs-3"
                  aria-label="Report failed"
                />
              </span>
            </Tooltip>
          );
        }

        const hasDownloadUrl = Boolean(params.row.presigned_url);
        const isDownloading = downloadingReportId === params.row.id;
        return (
          <Tooltip title={hasDownloadUrl ? "Download" : "File not ready"}>
            <span>
              <button
                type="button"
                className="btn btn-link p-0 border-0"
                disabled={!hasDownloadUrl || isDownloading}
                onClick={() => handleDownloadFile(params.row)}
                aria-label="Download report"
              >
                <i
                  className={`bi ${isDownloading ? "bi-hourglass-split" : "bi-download"} text-dark fs-3`}
                />
              </button>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  React.useEffect(() => {
    if (error) {
      toast.error(
        (error as any)?.data?.message || "Failed to load report download status."
      );
    }
  }, [error]);

  return (
    <div id="kt_app_content_container" className="app-container mx-5">
      <div className="card card-flush">
        <div
          className="card-header align-items-center py-1 gap-2 gap-md-5"
          style={{ justifyContent: "flex-start" }}
        >
          <button
            type="button"
            className="btn btn-link mb-2"
            onClick={() => navigate(-1)}
            style={{ marginRight: "-17px" }}
          >
            <i
              className="bi bi-arrow-90deg-left"
              style={{ fontSize: "20px", color: "black", cursor: "pointer" }}
            />
          </button>
          <span className="fs-3 text-gray-800 fw-bold ms-3">Exported Report</span>
        </div>

        <CardContent style={{ width: "100%", minHeight: "400px" }}>
          {isLoading ? (
            <LoadingSpinner />
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              pagination
              paginationMode="server"
              rowCount={totalRows}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              loading={isFetching}
              disableColumnMenu
              disableColumnResize
              autoHeight
              pageSizeOptions={[10, 20, 50]}
              sx={{
                width: "100%",
                maxWidth: "100%",
                "& .MuiDataGrid-columnHeader": {
                  backgroundColor: "lightgray",
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-cell": {
                  whiteSpace: "nowrap",
                },
                "& .MuiDataGrid-footerContainer": {
                  minHeight: "52px",
                },
                "& .MuiTablePagination-toolbar": {
                  display: "flex",
                  alignItems: "center",
                  minHeight: "52px",
                  padding: "0 8px",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                  {
                    margin: 0,
                    lineHeight: 1.43,
                    display: "flex",
                    alignItems: "center",
                  },
                "& .MuiTablePagination-select, & .MuiTablePagination-actions": {
                  marginTop: 0,
                  marginBottom: 0,
                },
                "& .MuiTablePagination-input": {
                  marginRight: "16px",
                },
                "& .MuiTablePagination-select .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  paddingTop: "6px",
                  paddingBottom: "6px",
                },
              }}
            />
          )}
        </CardContent>
      </div>
    </div>
  );
};

export default DownloaderReportList;
